// src/ui/screens/reviews.ts
// THE COACH REVIEW QUEUE: the surface that makes the second axis move.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// WHY THIS SCREEN EXISTS
// ══════════════════════════════════════════════════════════════════════════════════════════════
// The product's central claim is a TWO-AXIS honesty model:
//
//   measurement_class  EXPERIMENTAL | VISION_ESTIMATE | CALIBRATED_VISION | SENSOR_VERIFIED | LAB_VALIDATED
//                      — HOW the number was obtained. A camera does not become a laboratory.
//   review_state       AI_GENERATED | COACH_REVIEWED | COACH_CONFIRMED | COACH_CORRECTED
//                      — WHETHER a human has looked at it, and how far they went.
//
// Without a review surface, axis 2 is decorative: every number the Practice Lab produces stays
// AI_GENERATED for its whole life, the backend's whole append-only review apparatus (migration 041,
// two triggers, `sports_practice_coach_reviews`) is never written to, and "a coach checked this"
// is a claim the product cannot make about a single row. This screen is the client half of that.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// FOUR RULES THIS FILE IS BUILT AROUND, NOT MERELY COMPLIANT WITH
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
// 1 · THE AI'S ORIGINAL IS SHOWN ALONGSIDE A CORRECTION, NEVER REPLACED BY IT.
//     The defect this whole lane exists to not repeat is src/video_assessment/repo.ts:335 — the
//     coach's verdict written INTO the model's own column, destroying the model's output at exactly
//     the moment a human disagreed with it. The backend fixed that structurally: every review row
//     carries `ai_original_json`, a snapshot of what the model said, taken from the observation row
//     and never from a request body. A client that renders only the corrected number would restore
//     the defect at the glass — the record would be intact and the disagreement would still be
//     invisible. So the AI original is rendered on the observation, AND on every review row, AND
//     next to the effective value. Three places, all from the server.
//
// 2 · BOTH AXES, ALWAYS, AS TWO SEPARATE STATEMENTS.
//     There is no combined badge anywhere in this file and there must never be one. A
//     COACH_CONFIRMED VISION_ESTIMATE is a phone-camera estimate that a human agrees with; merging
//     the two into "verified" is the single most tempting lie this UI could tell. `axesBlock()` is
//     the only renderer for either axis and it emits two elements plus a sentence saying what a
//     review does and does not change.
//
// 3 · A CHANGED MIND IS A NEW REVIEW, NOT AN EDIT.
//     `sports_pl_reviews_append_only` (041:1179) raises on UPDATE and on DELETE. The UI matches that
//     shape: the button says "Record a new review", earlier reviews are never re-opened for editing,
//     and after a submit the chain is re-read from the server so the new row appears ABOVE the old
//     ones rather than in place of them.
//
// 4 · NOTHING IS FABRICATED — INCLUDING BY OMISSION.
//     No demo rows, no placeholder values, no optimistic local append after a POST (the chain is
//     re-read; if the re-read fails, the failure is shown). An empty queue says WHY it is empty.
//     A refusal is rendered as the refusal it is, in the server's own words.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// WHY THE API CALLS LIVE HERE AND NOT IN src/api/practice.ts
// ══════════════════════════════════════════════════════════════════════════════════════════════
// Five other lanes are editing this repo concurrently and `api/practice.ts` is shared ground. These
// five functions are transcribed from src/practice/routes/reviews.ts the same way `practice.ts` was
// transcribed from the other routers, and they obey the same shape rule that file states: THE
// SUBJECT IS A PATH PARAMETER AND THE ACTOR IS THE BEARER TOKEN. `ReviewSubmissionBody` below has no
// `coach_id`, no `coach_role`, no `athlete_id`, no `observation_id` and no `ai_original_json` — the
// exact five fields the backend's route comment says "are read by nothing in this file". They are
// not omitted by discipline; the type makes them unsendable. Moving these into `practice.ts` is a
// clean follow-up once the other lanes land.
//
// NO NEW RUNTIME DEPENDENCY. This screen is `el()` + `fetch` like every other screen; the app still
// ships zero runtime dependencies.

import { el, empty, field, mount, section, spinner } from '../dom';
import { refusalView } from '../refusal-view';
import { isRefusal, request, type ApiResult } from '../../api/client';
import { PRACTICE_NAMESPACE as NS } from '../../config';
import { getState } from '../state';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * VOCABULARY — spelled exactly as src/practice/review-service.ts spells it, which in turn is
 * spelled exactly as migration 041's CHECK constraints spell it. Nothing here is invented and
 * nothing is abbreviated for display: a value the coach picks is the value the database stores.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** review-service.ts:64 — the decisions a coach may take on an OBSERVATION (a number). */
export const OBSERVATION_DECISIONS = ['CONFIRM', 'ADJUST', 'DISMISS', 'COMMENT_ONLY'] as const;
export type ObservationDecision = (typeof OBSERVATION_DECISIONS)[number];

/**
 * review-service.ts:73 — deliberately NOT offered by this screen.
 *
 * 041 permits FLAG_NOT_A_DELIVERY and REQUEST_RECAPTURE, but its `..._delivery_only` CHECK requires
 * a delivery_id for them: they are statements about a BALL, not about a number, and
 * `validateSubmission` 409s them on an observation with a pointer. Rendering buttons that are
 * guaranteed to be refused would teach a coach that this screen is unreliable. They are named in a
 * note instead, so their absence reads as a decision rather than an oversight.
 */
export const DELIVERY_ONLY_DECISIONS = ['FLAG_NOT_A_DELIVERY', 'REQUEST_RECAPTURE'] as const;

/** review-service.ts:76 — a coach-entered number must say on what basis it was arrived at. */
export const CORRECTED_EVIDENCE = ['COACH_JUDGEMENT', 'COACH_MEASURED', 'COACH_RECALL', 'THIRD_PARTY_DEVICE'] as const;
export type CorrectedEvidence = (typeof CORRECTED_EVIDENCE)[number];

/** metric-cards.ts:81 — AXIS 2. Used only as a filter vocabulary; this screen never invents one. */
export const REVIEW_STATES = ['AI_GENERATED', 'COACH_REVIEWED', 'COACH_CONFIRMED', 'COACH_CORRECTED'] as const;
export type ReviewState = (typeof REVIEW_STATES)[number];

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * WIRE TYPES — the response shapes, transcribed from review-service.ts.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface ReviewQueueItem {
  observation_id: string;
  athlete_id: string;
  session_id: string;
  delivery_id: string | null;
  metric_id: string;
  metric_card_version: number;
  /** AXIS 1. Never changed by a review — the freeze trigger refuses an UPDATE that touches it. */
  measurement_class: string;
  /** AXIS 2. The only axis a review moves. */
  review_state: ReviewState;
  observation_state: 'MEASURED' | 'REFUSED';
  refusal_reason_code: string | null;
  ai_value_num: number | null;
  ai_value_low: number | null;
  ai_value_high: number | null;
  ai_value_category: string | null;
  unit: string | null;
  ai_confidence: number | null;
  observed_at: string;
  /** false on a REFUSED observation: there is no value to adjust. Rendered, never assumed. */
  adjustable: boolean;
}

export interface AiOriginalSnapshot {
  snapshot_of: string;
  observation_id: string;
  metric_id: string;
  measurement_class: string;
  model_version: string | null;
  observation_state: 'MEASURED' | 'REFUSED';
  refusal_reason_code: string | null;
  ai_value_num: number | null;
  ai_value_low: number | null;
  ai_value_high: number | null;
  ai_value_category: string | null;
  unit: string | null;
  ai_confidence: number | null;
  observed_at: string;
  captured_at: string;
  [k: string]: unknown;
}

export interface ObservationRow {
  id: string;
  athlete_id: string;
  session_id: string;
  metric_id: string;
  metric_card_version: number;
  measurement_class: string;
  evidence_class: string;
  model_version: string | null;
  review_state: ReviewState;
  reviewed_at: string | null;
  observation_state: 'MEASURED' | 'REFUSED';
  refusal_reason_code: string | null;
  refusal_detail: string | null;
  ai_value_num: number | null;
  ai_value_low: number | null;
  ai_value_high: number | null;
  ai_value_category: string | null;
  unit: string | null;
  uncertainty_num: number | null;
  uncertainty_kind: string | null;
  ai_confidence: number | null;
  observed_at: string;
}

export interface CoachReviewRow {
  id: string;
  observation_id: string | null;
  coach_id: string | null;
  coach_role: string;
  decision: string;
  comment: string | null;
  drill_id: string | null;
  /** THE RETAINED ORIGINAL. What the model said, at the moment this coach reacted to it. */
  ai_original_json: AiOriginalSnapshot;
  prior_review_state: ReviewState;
  corrected_value_num: number | null;
  corrected_value_low: number | null;
  corrected_value_high: number | null;
  corrected_value_category: string | null;
  corrected_unit: string | null;
  corrected_evidence: CorrectedEvidence | null;
  reviewed_at: string;
}

export interface EffectiveValue {
  effective_value_num: number | null;
  effective_value_low: number | null;
  effective_value_high: number | null;
  effective_value_category: string | null;
  effective_unit: string | null;
  effective_evidence: CorrectedEvidence | null;
  is_coach_corrected: boolean;
  effective_review_id: string | null;
  effective_coach_id: string | null;
  effective_reviewed_at: string | null;
}

export interface DrillRow {
  id: string;
  drill_key: string;
  drill_version: number;
  title: string;
  body: string;
  contraindications: string;
  min_age: number | null;
  max_age: number | null;
  status: string;
}

export interface SubmittedReview {
  review: CoachReviewRow;
  observation: ObservationRow;
  effective: EffectiveValue;
  drill: { drill_id: string; drill_key: string; drill_version: number; title: string; contraindications: string } | null;
  /** The backend's own sentence naming BOTH axes. Rendered verbatim — see `describe()` there. */
  note: string;
}

/**
 * THE REQUEST BODY. Note what is absent — see the header block. There is no field on this type
 * capable of carrying an identity claim, so no screen, however careless, can send one.
 */
export interface ReviewSubmissionBody {
  decision: ObservationDecision;
  comment?: string | null;
  corrected?: {
    value_num?: number | null;
    value_low?: number | null;
    value_high?: number | null;
    value_category?: string | null;
    unit?: string | null;
    evidence?: CorrectedEvidence;
  } | null;
  /** Exactly one drill, pinned by row id or by (key, version). There is no "latest". */
  drill?: { drill_id: string } | { drill_key: string; drill_version: number } | null;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * API — five calls, all under /v1/practice/**.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface QueueQuery {
  /** Defaults to AI_GENERATED server-side — the PENDING work. 'ALL' shows the whole surface. */
  state?: ReviewState | 'ALL';
  sessionId?: string | null;
  limit?: number;
}

/** GET /v1/practice/review-queue — every observation awaiting this coach, across their athletes. */
export function coachReviewQueue(q: QueueQuery = {}): Promise<ApiResult<{ athlete_count: number; items: ReviewQueueItem[] }>> {
  return request(`${NS}/review-queue`, { query: { state: q.state ?? null, session_id: q.sessionId ?? null, limit: q.limit ?? null } });
}

/**
 * GET …/athletes/:id/review-queue — one athlete's queue.
 *
 * `may_review` is the server's answer to "may THIS caller review THIS athlete's numbers", and it is
 * NOT the same question as "may they read them": an owner and a verified guardian may read, and are
 * not reviewers of a measurement about themselves or their child. This screen keys its controls on
 * that flag and never on the role string.
 */
export function athleteReviewQueue(
  athleteId: string,
  q: QueueQuery = {},
): Promise<ApiResult<{ role: string; may_review: boolean; items: ReviewQueueItem[] }>> {
  return request(`${NS}/athletes/${encodeURIComponent(athleteId)}/review-queue`, {
    query: { state: q.state ?? null, session_id: q.sessionId ?? null, limit: q.limit ?? null },
  });
}

/** GET …/observations/:id/reviews — the frozen AI output, the whole append-only chain, the effective value. */
export function observationReviews(
  athleteId: string,
  observationId: string,
): Promise<ApiResult<{ role: string; observation: ObservationRow; reviews: CoachReviewRow[]; effective: EffectiveValue }>> {
  return request(
    `${NS}/athletes/${encodeURIComponent(athleteId)}/observations/${encodeURIComponent(observationId)}/reviews`,
  );
}

/**
 * POST …/observations/:id/reviews — THE APPEND.
 *
 * Deliberately performs NO client-side validation of its own. The client-side rules live in
 * `reviewDraftProblems()` and are applied by the FORM, so that this function can be called directly
 * — by a test, or by any future caller — and meet the server's own refusal. A guard baked in here
 * would make the server's identical rule untestable from this app, and an untested server rule is
 * indistinguishable from an absent one.
 */
export function submitObservationReview(
  athleteId: string,
  observationId: string,
  body: ReviewSubmissionBody,
): Promise<ApiResult<SubmittedReview>> {
  return request(
    `${NS}/athletes/${encodeURIComponent(athleteId)}/observations/${encodeURIComponent(observationId)}/reviews`,
    { method: 'POST', body },
  );
}

/** GET /v1/practice/drills — the curated, coach-authored library. Published rows only, server-side. */
export function listPublishedDrills(q: { fault?: string | null; pattern?: string | null } = {}): Promise<ApiResult<DrillRow[] | { drills: DrillRow[] }>> {
  return request(`${NS}/drills`, { query: { fault: q.fault ?? null, pattern: q.pattern ?? null } });
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE CLIENT-SIDE GUARD
 *
 * Every rule below is ALSO enforced by review-service.ts:767 `validateSubmission` and, underneath
 * that, by a CHECK constraint in migration 041. This is the third of three, and it is the weakest
 * one — it exists to tell a coach what is missing BEFORE a round trip, not to be the authority. That
 * is why it is a pure function returning SENTENCES rather than a boolean: a boolean would let the
 * form say "invalid" and leave the coach guessing which of four things was wrong.
 *
 * It is exported because a rule that cannot be called in isolation cannot be tested in isolation.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface ReviewDraft {
  decision: ObservationDecision | '';
  comment: string;
  value_num: string;
  value_low: string;
  value_high: string;
  value_category: string;
  unit: string;
  /** '' means the coach has not chosen a basis. There is no default; see `evidenceSelect()`. */
  evidence: CorrectedEvidence | '';
  /** From the server's `adjustable` / `observation_state`. A REFUSED row carries no value. */
  adjustable: boolean;
  drillId: string;
}

export function emptyDraft(adjustable: boolean): ReviewDraft {
  return {
    decision: '', comment: '', value_num: '', value_low: '', value_high: '',
    value_category: '', unit: '', evidence: '', adjustable, drillId: '',
  };
}

const filled = (s: string): boolean => s.trim().length > 0;
const asNum = (s: string): number | null => (filled(s) && Number.isFinite(Number(s)) ? Number(s) : null);

/** Every reason this draft cannot be sent, in the order a coach would meet them. Empty ⇒ sendable. */
export function reviewDraftProblems(d: ReviewDraft): string[] {
  const problems: string[] = [];
  const hasPoint = filled(d.value_num);
  const hasRange = filled(d.value_low) && filled(d.value_high);
  const hasCategory = filled(d.value_category);
  const anyCorrection = hasPoint || hasRange || hasCategory || filled(d.value_low) || filled(d.value_high);

  if (d.decision === '') {
    problems.push(`Choose a decision first: ${OBSERVATION_DECISIONS.join(', ')}.`);
    return problems; // nothing else can be judged until the decision is known
  }

  if (d.decision === 'ADJUST') {
    if (!d.adjustable) {
      problems.push(
        'This observation is REFUSED — the capture failed and the system declined to produce a '
        + 'number, so there is nothing to adjust. Confirm the refusal, comment on it, or dismiss it; '
        + 'a re-measurement is a recapture, not an edit.');
    }
    if (!hasPoint && !hasRange && !hasCategory) {
      problems.push('An ADJUST needs a corrected value: a number, or BOTH ends of a range, or a category.');
    }
    if (filled(d.value_low) !== filled(d.value_high)) {
      problems.push('A corrected range needs both ends. One end alone is not a range.');
    }
    if (hasRange && Number(d.value_low) > Number(d.value_high)) {
      problems.push('The low end of the corrected range must not be greater than the high end.');
    }
    // ── THE ONE THIS SCREEN EXISTS TO MAKE UNMISSABLE ──────────────────────────────────────────
    // A coach's number is not a vision estimate and does not inherit the AI's measurement class. If
    // the basis were defaulted, every correction in the database would claim whatever the default
    // was, and the corrected_evidence column would carry no information at all.
    if (d.evidence === '') {
      problems.push(
        'An ADJUST must state the BASIS for the corrected value — one of '
        + `${CORRECTED_EVIDENCE.join(', ')}. A coach's number does not inherit how the AI measured; `
        + 'it is recorded on its own terms or not at all.');
    }
  } else if (anyCorrection || d.evidence !== '') {
    // 041's `no_stray_correction` CHECK: a corrected value only ever travels with an ADJUST.
    problems.push(`A ${d.decision} carries no corrected value. Clear the correction fields, or choose ADJUST.`);
  }

  if (d.decision === 'COMMENT_ONLY' && !filled(d.comment)) {
    problems.push('A COMMENT_ONLY review needs a comment — it asserts nothing else.');
  }
  return problems;
}

/**
 * The draft, as the request body. Only reachable when `reviewDraftProblems` is empty (the form
 * enforces that), and it still cannot express an identity claim — `ReviewSubmissionBody` has no
 * field for one.
 *
 * `unit` is omitted when blank rather than sent as '': review-service.ts defaults corrected_unit to
 * the observation's own unit, and a blank string would overwrite that with nothing.
 */
export function buildSubmission(d: ReviewDraft): ReviewSubmissionBody {
  const base: ReviewSubmissionBody = {
    decision: d.decision as ObservationDecision,
    comment: filled(d.comment) ? d.comment.trim() : null,
    corrected: null,
    drill: filled(d.drillId) ? { drill_id: d.drillId } : null,
  };
  if (d.decision !== 'ADJUST') return base;
  return {
    ...base,
    corrected: {
      value_num: asNum(d.value_num),
      value_low: asNum(d.value_low),
      value_high: asNum(d.value_high),
      value_category: filled(d.value_category) ? d.value_category.trim() : null,
      ...(filled(d.unit) ? { unit: d.unit.trim() } : {}),
      // Non-null by construction: `reviewDraftProblems` refuses an empty basis, and the form will
      // not call this until that list is empty.
      evidence: d.evidence as CorrectedEvidence,
    },
  };
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * SCREEN STATE — module-local, deliberately.
 *
 * NOT added to ui/state.ts. That module's `selectAthlete` requires a `Provenance` from one of two
 * enumerating surfaces, and widening it is not this lane's to do. The rule it encodes is upheld here
 * anyway and for the same reason: `selection.athleteId` is only ever an id that came out of a review
 * queue RESPONSE — there is no code path in this file that puts a typed or guessed athlete id into a
 * request.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

interface Selection {
  athleteId: string;
  observationId: string;
  /**
   * Carried from the queue that produced this row, because the observation-chain read cannot answer
   * it: its `role` is the MEDIA-access role, and a coach who is also the guardian of their own child
   * resolves as 'guardian' there (see `mayCoach` in review-service.ts). Keying controls on `role`
   * would hide the review buttons from exactly that coach.
   */
  mayReview: boolean;
}

let filterState: ReviewState | 'ALL' = 'AI_GENERATED';
let selection: Selection | null = null;

/** Test-only reset, matching the house pattern (`__clearAll`, `__resetDeviceIdCache`). */
export function __resetReviewsScreen(): void {
  filterState = 'AI_GENERATED';
  selection = null;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * RENDERING
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** A value as a string, or '' when there is nothing — `field()` renders '—'. Never a fabricated 0. */
function valueText(v: {
  num: number | null; low: number | null; high: number | null; category: string | null; unit: string | null;
}): string {
  const bits: string[] = [];
  if (v.num !== null && v.num !== undefined) bits.push(String(v.num));
  if (v.low !== null && v.low !== undefined && v.high !== null && v.high !== undefined) bits.push(`${v.low}–${v.high}`);
  if (v.category) bits.push(v.category);
  if (bits.length === 0) return '';
  return v.unit ? `${bits.join(' · ')} ${v.unit}` : bits.join(' · ');
}

/**
 * THE TWO AXES. Two elements, one sentence, no merged badge — ever.
 *
 * This is the only place in this file that renders either axis, so the guarantee is structural
 * rather than a habit spread over ten call sites. The sentence is deliberately true for every
 * measurement class: it names the class the server sent and states what a review does NOT do,
 * without this client holding any opinion about which classes are "good enough".
 */
function axesBlock(measurementClass: string, reviewState: string): HTMLElement {
  return el('div', { class: 'row' },
    el('span', { class: 'tag' }, `measured: ${measurementClass}`),
    el('span', { class: 'tag' }, `reviewed: ${reviewState}`),
    el('p', { class: 'note' },
      `How this was measured: ${measurementClass}. Whether a human has checked it: ${reviewState}. `
      + 'These are two separate facts and this screen never merges them — a coach agreeing with a '
      + `number does not re-measure it, so a reviewed ${measurementClass} is still a `
      + `${measurementClass}.`),
  );
}

/** The AI's own output, labelled as the model's, wherever it is shown. */
function aiOriginalBlock(title: string, o: {
  ai_value_num: number | null; ai_value_low: number | null; ai_value_high: number | null;
  ai_value_category: string | null; unit: string | null; ai_confidence: number | null;
  measurement_class: string; model_version: string | null; observation_state: string;
  refusal_reason_code: string | null;
}): HTMLElement {
  return el('div', { class: 'row' },
    el('h3', {}, title),
    field('AI value', valueText({
      num: o.ai_value_num, low: o.ai_value_low, high: o.ai_value_high,
      category: o.ai_value_category, unit: o.unit,
    })),
    field('AI confidence', o.ai_confidence),
    field('Measurement class', o.measurement_class),
    field('Model version', o.model_version),
    field('Observation state', o.observation_state),
    o.observation_state === 'REFUSED' && el('p', { class: 'warn' },
      `The capture failed and the system refused to produce a number (${o.refusal_reason_code ?? 'no reason code'}). `
      + 'That refusal is the measurement.'),
  );
}

/* ── THE QUEUE ─────────────────────────────────────────────────────────────────────────────── */

function queueRow(item: ReviewQueueItem, mayReview: boolean, rerender: () => void): HTMLElement {
  const open = selection?.observationId === item.observation_id;
  return el('button', {
    class: `row-item row-tap${open ? ' row-selected' : ''}`,
    onclick: () => {
      // The athlete id comes from THIS response. It was never typed, guessed or carried across.
      selection = { athleteId: item.athlete_id, observationId: item.observation_id, mayReview };
      rerender();
    },
  },
    el('p', { class: 'mono small' }, `${item.metric_id} v${item.metric_card_version}`),
    axesBlock(item.measurement_class, item.review_state),
    field('AI value', valueText({
      num: item.ai_value_num, low: item.ai_value_low, high: item.ai_value_high,
      category: item.ai_value_category, unit: item.unit,
    })),
    field('Observed at', item.observed_at),
    el('p', { class: 'mono small' }, `athlete ${item.athlete_id} · session ${item.session_id}`),
    item.observation_state === 'REFUSED' && el('span', { class: 'tag tag-held' }, 'refused capture — not adjustable'),
    open && el('span', { class: 'tag tag-ok' }, 'open below'),
  );
}

function stateFilter(rerender: () => void): HTMLElement {
  const sel = el('select', { class: 'input' },
    ...REVIEW_STATES.map((s) => el('option', { value: s, ...(s === filterState ? { selected: true } : {}) }, s)),
    el('option', { value: 'ALL', ...(filterState === 'ALL' ? { selected: true } : {}) }, 'ALL'),
  );
  sel.addEventListener('change', () => {
    filterState = sel.value as ReviewState | 'ALL';
    selection = null; // the open row may not be in the new filter; do not keep showing it as "in" it
    rerender();
  });
  return el('div', {},
    el('label', { class: 'label' }, 'Show observations whose review_state is'),
    sel,
    el('p', { class: 'note' },
      'AI_GENERATED is the pending work — nobody has looked at these yet. This filter is a view over '
      + 'axis 2 only; it never changes how anything was measured.'),
  );
}

/* ── THE OBSERVATION PANEL ─────────────────────────────────────────────────────────────────── */

async function observationPanel(sel: Selection, rerender: () => void): Promise<HTMLElement> {
  const first = await observationReviews(sel.athleteId, sel.observationId);
  if (isRefusal(first)) return section('Observation', refusalView(first, { onRetry: rerender }));

  const obs = first.value.observation;
  const head = el('div');
  const chain = el('div');
  const result = el('div');

  /**
   * The header is RE-RENDERED after a review, not left as it was.
   *
   * It was left as it was in the first cut of this screen, and the suite caught it: after a
   * correction the panel still read "Corrected by a coach: no" with the AI's number as the effective
   * value, because those blocks were built once from the read that opened the panel. A stale
   * "nobody has corrected this" is a false statement about a row that has just been corrected — the
   * same class of untruth as hiding the AI original, arriving from the opposite direction.
   */
  const renderHead = (o: ObservationRow, effective: EffectiveValue): void => {
    mount(head,
      el('p', { class: 'mono small' }, `${o.metric_id} v${o.metric_card_version} · observation ${o.id}`),
      axesBlock(o.measurement_class, o.review_state),

      // ── 1 · THE MODEL'S OUTPUT. Shown first, and shown whether or not it was later corrected.
      aiOriginalBlock('What the AI said', o),
      field('Uncertainty', o.uncertainty_num === null ? '' : `${o.uncertainty_num} (${o.uncertainty_kind ?? 'kind not stated'})`),

      // ── 2 · THE EFFECTIVE VALUE. Resolved at READ time from the latest correction; the observation
      //        row itself was never overwritten, which is what makes both numbers available at once.
      el('h3', {}, 'The value in effect now'),
      field('Effective value', valueText({
        num: effective.effective_value_num, low: effective.effective_value_low,
        high: effective.effective_value_high, category: effective.effective_value_category,
        unit: effective.effective_unit,
      })),
      field('Basis', effective.effective_evidence),
      field('Corrected by a coach', effective.is_coach_corrected ? 'yes' : 'no'),
      effective.is_coach_corrected && el('p', { class: 'note' },
        'A coach replaced this number. The AI output above is unchanged and stays on the record — the '
        + 'disagreement between the two is kept on purpose.'),
    );
  };

  const renderChain = (reviews: CoachReviewRow[]): void => {
    mount(chain,
      el('h3', {}, `Review history — ${reviews.length} review${reviews.length === 1 ? '' : 's'}, newest first`),
      el('p', { class: 'note' },
        'This log is append-only on the server: an UPDATE or a DELETE against it raises. A coach who '
        + 'changes their mind adds a NEW review; nothing below is ever edited or removed, which is '
        + 'why an earlier disagreement survives a later agreement.'),
      reviews.length
        ? el('div', { class: 'list' }, ...reviews.map(reviewRow))
        : empty('No human has reviewed this observation yet. Its review_state is AI_GENERATED because '
          + 'that is exactly what it is.'),
    );
  };

  renderHead(obs, first.value.effective);
  renderChain(first.value.reviews);

  const refresh = async (): Promise<void> => {
    // Re-READ, never a local append. An optimistic client-side push would show a row the server may
    // not hold — the one place this screen could invent data, closed deliberately.
    const again = await observationReviews(sel.athleteId, sel.observationId);
    if (isRefusal(again)) {
      mount(chain,
        el('p', { class: 'warn' }, 'The review was recorded, but re-reading this observation failed, '
          + 'so what is shown above is now older than the record:'),
        refusalView(again));
      return;
    }
    renderHead(again.value.observation, again.value.effective);
    renderChain(again.value.reviews);
  };

  return section('Observation under review',
    head,
    chain,

    // ── 3 · THE ACT.
    sel.mayReview
      ? await decisionForm(sel, obs, rerender, result, refresh)
      : section('Reviewing',
        empty('The server says you may read this observation but not review it. An owner and a '
          + 'verified guardian may see these numbers; a measurement confirmed by its own subject is '
          + 'not a reviewed measurement, so the review controls are not offered here.')),
    result,
  );
}

function reviewRow(rv: CoachReviewRow): HTMLElement {
  const ai = rv.ai_original_json ?? ({} as AiOriginalSnapshot);
  return el('div', { class: 'row-item' },
    el('span', { class: 'tag' }, rv.decision),
    field('Reviewed at', rv.reviewed_at),
    field('Coach', rv.coach_id),
    field('Coach role', rv.coach_role),
    field('review_state before this review', rv.prior_review_state),
    rv.comment && el('p', { class: 'note' }, rv.comment),
    rv.decision === 'ADJUST' && el('div', {},
      field('Coach corrected to', valueText({
        num: rv.corrected_value_num, low: rv.corrected_value_low, high: rv.corrected_value_high,
        category: rv.corrected_value_category, unit: rv.corrected_unit,
      })),
      field('Stated basis', rv.corrected_evidence),
    ),
    // ── THE POINT OF THE WHOLE LANE: what the model said, kept ON the row that disagreed with it.
    el('div', { class: 'row' },
      field('AI original on this review', valueText({
        num: ai.ai_value_num ?? null, low: ai.ai_value_low ?? null, high: ai.ai_value_high ?? null,
        category: ai.ai_value_category ?? null, unit: ai.unit ?? null,
      })),
      field('AI original measurement class', ai.measurement_class ?? ''),
      field('AI original model version', ai.model_version ?? ''),
    ),
    rv.drill_id && field('Drill pinned', rv.drill_id),
  );
}

async function decisionForm(
  sel: Selection,
  obs: ObservationRow,
  rerender: () => void,
  result: HTMLElement,
  refresh: () => Promise<void>,
): Promise<HTMLElement> {
  const adjustable = obs.observation_state === 'MEASURED';
  let draft = emptyDraft(adjustable);
  const form = el('div');
  const drills = await loadDrills();

  const render = (): void => {
    const problems = reviewDraftProblems(draft);
    mount(form,
      el('p', { class: 'note' },
        'Recording a review INSERTS a new row. It does not edit this observation: the AI values, the '
        + 'unit, the model version and the measurement class are all frozen by a database trigger, '
        + 'and the only column a review moves is review_state.'),

      el('label', { class: 'label' }, 'Decision'),
      el('div', { class: 'row' }, ...OBSERVATION_DECISIONS.map((d) => el('button', {
        class: `btn${draft.decision === d ? ' btn-primary' : ' btn-quiet'}`,
        onclick: () => { draft.decision = d; render(); },
      }, d))),
      el('p', { class: 'note' },
        `${DELIVERY_ONLY_DECISIONS.join(' and ')} are not offered here: they are statements about a `
        + 'BALL, not about a number, and the server refuses them on an observation. Raise them on the '
        + 'delivery instead.'),

      draft.decision === 'ADJUST' && adjustFields(draft, obs, render),
      textField('Comment', draft.comment, (v) => { draft.comment = v; }),
      drillPicker(drills, draft, render),

      problems.length
        ? el('div', { class: 'warn' },
          el('p', {}, 'Not sent — this app has not contacted the server, because:'),
          ...problems.map((p) => el('p', {}, p)),
          el('p', { class: 'note' },
            'The server enforces every one of these rules independently, and a database CHECK '
            + 'enforces them under that. This message is the earliest of the three, not the only one.'))
        : el('p', { class: 'note' }, 'Ready to record.'),

      el('button', {
        class: 'btn btn-primary',
        onclick: () => {
          const now = reviewDraftProblems(draft);
          if (now.length) { render(); return; } // refuse locally; nothing leaves the device
          void (async () => {
            mount(result, spinner('Recording the review…'));
            const r = await submitObservationReview(sel.athleteId, sel.observationId, buildSubmission(draft));
            if (isRefusal(r)) {
              // The server's own sentence, verbatim, as the refusal it is.
              mount(result, refusalView(r, { onRetry: () => rerender() }));
              return;
            }
            mount(result,
              el('p', { class: 'ok' }, `Review ${r.value.review.id} recorded.`),
              // The backend writes a sentence naming both axes. Show it rather than a word of ours.
              el('p', { class: 'note' }, r.value.note),
              axesBlock(r.value.observation.measurement_class, r.value.observation.review_state),
              r.value.drill && el('p', { class: 'note' },
                `Drill pinned: ${r.value.drill.drill_key} v${r.value.drill.drill_version} — ${r.value.drill.title}. `
                + `When NOT to do it: ${r.value.drill.contraindications}`),
            );
            // A REVIEW IS ONE ACT. The form goes back to empty — including the decision, which is
            // never pre-chosen — because the next review is a separate judgement, not a continuation
            // of this one. Leaving the ADJUST fields populated would also make the very next
            // CONFIRM unsendable (041's `no_stray_correction`), which is a confusing way to be right.
            draft = emptyDraft(adjustable);
            render();
            await refresh();
          })();
        },
      }, 'Record a new review'),
      el('p', { class: 'note' },
        'The button says NEW because that is what happens. If you reviewed this observation before, '
        + 'that review stays exactly as it was and this one is added after it.'),
    );
  };
  render();
  return section('Your review', form);
}

function adjustFields(draft: ReviewDraft, obs: ObservationRow, render: () => void): HTMLElement {
  return el('div', { class: 'form' },
    el('p', { class: 'note' },
      'Your number goes on the review row, beside the AI\'s. The AI\'s output above is not replaced, '
      + 'moved or hidden — the difference between the two is the most useful thing this product records.'),
    numField('Corrected value', draft.value_num, (v) => { draft.value_num = v; }),
    numField('…or range low', draft.value_low, (v) => { draft.value_low = v; }),
    numField('…and range high', draft.value_high, (v) => { draft.value_high = v; }),
    textField('…or a category', draft.value_category, (v) => { draft.value_category = v; }),
    textField(`Unit (blank keeps the observation's own: ${obs.unit ?? 'none recorded'})`, draft.unit, (v) => { draft.unit = v; }),
    evidenceSelect(draft, render),
  );
}

/**
 * THE BASIS. A real choice, structurally.
 *
 * The first option carries value '' and is selected, so the element opens on "no basis chosen" and a
 * coach who ignores this control produces a draft the guard refuses. There is no pre-selected
 * CORRECTED_EVIDENCE anywhere in this file — a defaulted basis would put a value the coach never
 * stated into a column whose entire purpose is to record what the coach stated.
 */
function evidenceSelect(draft: ReviewDraft, render: () => void): HTMLElement {
  const sel = el('select', { class: 'input' },
    el('option', { value: '', selected: true }, '— choose the basis for your number —'),
    ...CORRECTED_EVIDENCE.map((e) => el('option', { value: e, ...(draft.evidence === e ? { selected: true } : {}) }, e)),
  );
  sel.addEventListener('change', () => { draft.evidence = sel.value as CorrectedEvidence | ''; render(); });
  return el('div', {},
    el('label', { class: 'label' }, 'On what basis? (required for an ADJUST)'),
    sel,
    el('p', { class: 'note' },
      'Recorded on the review as corrected_evidence. It never inherits the AI\'s measurement class: '
      + 'a number you measured with a tape is not the same kind of fact as one you recall, and the '
      + 'record is required to be able to tell them apart.'),
  );
}

function drillPicker(drills: DrillRow[] | null, draft: ReviewDraft, render: () => void): HTMLElement {
  if (drills === null) {
    return el('p', { class: 'note' }, 'The drill library could not be read, so no drill can be attached to this review.');
  }
  if (drills.length === 0) {
    return el('p', { class: 'note' },
      'The coach-authored drill library returned no published drills, so there is nothing to prescribe. '
      + 'Neither this app nor the AI Coach invents one.');
  }
  const sel = el('select', { class: 'input' },
    el('option', { value: '', selected: true }, '— no drill —'),
    ...drills.map((d) => el('option', { value: d.id, ...(draft.drillId === d.id ? { selected: true } : {}) },
      `${d.drill_key} v${d.drill_version} — ${d.title}`)),
  );
  sel.addEventListener('change', () => { draft.drillId = sel.value; render(); });
  const chosen = drills.find((d) => d.id === draft.drillId) ?? null;
  return el('div', {},
    el('label', { class: 'label' }, 'Attach a drill (optional)'),
    sel,
    el('p', { class: 'note' },
      'Only published, coach-authored drills, pinned to an exact version — a later edit to the '
      + 'library cannot rewrite what was prescribed here, and there is no "latest".'),
    chosen && el('p', { class: 'warn' }, `When NOT to do it: ${chosen.contraindications}`),
  );
}

async function loadDrills(): Promise<DrillRow[] | null> {
  const r = await listPublishedDrills();
  if (isRefusal(r)) return null;
  const v = r.value;
  return Array.isArray(v) ? v : (v.drills ?? []);
}

function textField(label: string, value: string, onChange: (v: string) => void): HTMLElement {
  const input = el('input', { class: 'input', type: 'text', value });
  input.addEventListener('input', () => onChange(input.value));
  return el('div', {}, el('label', { class: 'label' }, label), input);
}

function numField(label: string, value: string, onChange: (v: string) => void): HTMLElement {
  const input = el('input', { class: 'input mono', type: 'number', step: 'any', value });
  input.addEventListener('input', () => onChange(input.value));
  return el('div', {}, el('label', { class: 'label' }, label), input);
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE SCREEN
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export async function reviewsScreen(root: HTMLElement, rerender: () => void): Promise<void> {
  const body = el('div');

  mount(root,
    section('Review queue',
      el('p', { class: 'note' },
        'Every number in the Practice Lab carries two facts: HOW it was measured, and WHETHER a human '
        + 'has checked it. Nothing in this product moves the second one except this screen. An '
        + 'observation nobody reviews stays AI_GENERATED for ever, and that is the honest label for it.'),
      stateFilter(rerender),
    ),
    body,
  );

  body.append(spinner('Loading everything waiting for you…'));
  const st = getState();
  const [queue, mine] = await Promise.all([
    coachReviewQueue({ state: filterState }),
    // Only when a screen elsewhere has already selected an athlete through the provenance-checked
    // path. This screen never types an athlete id of its own.
    st.athlete ? athleteReviewQueue(st.athlete.athleteId, { state: filterState }) : Promise.resolve(null),
  ]);

  const out = el('div');

  /* ── THE CROSS-ATHLETE QUEUE ──────────────────────────────────────────────────────────────── */
  if (isRefusal(queue)) {
    out.append(section('Across your athletes', refusalView(queue, { onRetry: rerender })));
  } else {
    const q = queue.value;
    out.append(section('Across your athletes',
      field('Athletes you may coach', q.athlete_count),
      field('Observations in this view', q.items.length),
      el('p', { class: 'note' },
        'The server enumerates your academies\' rosters and then re-checks every athlete one at a '
        + 'time, so a fault in the enumeration can only ever show you FEWER athletes — never a '
        + 'stranger\'s child.'),
      q.items.length
        ? el('div', { class: 'list' }, ...q.items.map((i) => queueRow(i, true, rerender)))
        : empty(q.athlete_count === 0
          ? 'You do not coach anybody the server can see, so this queue is empty. That is an answer, '
            + 'not a loading state.'
          : `Nothing is in review_state ${filterState} for your athletes right now.`),
    ));
  }

  /* ── ONE ATHLETE'S QUEUE ──────────────────────────────────────────────────────────────────── */
  if (mine && st.athlete) {
    if (isRefusal(mine)) {
      out.append(section(`Selected athlete — ${st.athlete.label}`, refusalView(mine, { onRetry: rerender })));
    } else {
      const m = mine.value;
      out.append(section(`Selected athlete — ${st.athlete.label}`,
        field('Your access role', m.role),
        field('May review', m.may_review ? 'yes' : 'no'),
        !m.may_review && el('p', { class: 'note' },
          'You may read this athlete\'s observations. Reviewing them is a different permission: the '
          + 'subject of a measurement, and their guardian, are not its reviewers.'),
        m.items.length
          ? el('div', { class: 'list' }, ...m.items.map((i) => queueRow(i, m.may_review, rerender)))
          : empty(`Nothing in review_state ${filterState} for this athlete.`),
      ));
    }
  }

  /* ── THE OPEN OBSERVATION ─────────────────────────────────────────────────────────────────── */
  if (selection) out.append(await observationPanel(selection, rerender));

  mount(body, out);
}
